import { format, parseISO } from 'date-fns';
import { AxiosRequestConfig, AxiosResponse } from 'axios';
import HttpClient from '../HttpClient';
import HTTPResponse from '../HttpResponse';
import { Errors, ErrorResponse } from './errors';
import { authenticateUser } from '../../helpers/authTokenHelper';
import {
  TimeslotsResponse,
  toAvailableService,
  ServicesResponse,
  toAvailableStore,
  AvailableStore,
  StoresResponse,
  toSlots,
  AppointmentResponse,
  PayOrderLinkResponse,
  Slots,
  Payload,
} from './model';
import {
  isStoreService,
  ServiceLocation,
  showTimeInStoreTZ,
} from '../../helpers';

/* globals CSS_CONFIG */
const { SERVICES } = CSS_CONFIG;

class Appointment {
  private httpTW: HttpClient;

  private httpBoka: HttpClient;

  public constructor(httpTW: HttpClient, httpBoka: HttpClient) {
    this.httpTW = httpTW;
    this.httpBoka = httpBoka;
  }

  public getAvailableServices = async (
    zip: string,
    operation: string,
    stateCode?: string,
  ): Promise<HTTPResponse<ServicesResponse>> => {
    let servicesResponse: ServicesResponse = { services: [] };
    try {
      // operation needs to map with backend enum code
      let url = `/servicesbygroup/${zip}/${operation.toUpperCase()}`;
      if (SERVICES.getServicesUsingStateCode && stateCode) {
        url = `${url}?stateCode=${stateCode}`;
      }
      const result = await this.httpTW.get(url);

      if (!Array.isArray(result.data) || result.data.length === 0) {
        return new HTTPResponse(
          servicesResponse,
          Errors.SERVICE_NO_LONGER_POSSIBLE,
        );
      }

      servicesResponse = {
        services: result.data.map(toAvailableService),
      };

      return new HTTPResponse(servicesResponse);
    } catch (err) {
      const { response } = err;
      if (!response) {
        return new HTTPResponse(servicesResponse, Errors.SERVICE_NOT_AVAILABLE);
      }

      const error =
        Object.values(Errors).indexOf(response.status) > -1
          ? response.status
          : Errors.SERVICE_NOT_AVAILABLE;
      return new HTTPResponse(servicesResponse, error);
    }
  };

  public getAvailableTimeslots = async (
    zip: string,
    type: string,
    location: ServiceLocation,
    storeId: string,
    from: string,
    serviceCode: string,
  ): Promise<HTTPResponse<TimeslotsResponse>> => {
    let availableTimeslotsResponse: TimeslotsResponse = {
      timeSlots: [],
    };

    let url =
      isStoreService(location) && storeId
        ? `/services/${zip}/${type}/${storeId}?serviceCode=${serviceCode}`
        : `/services/${zip}/${type}?serviceCode=${serviceCode}`;

    const formattedFrom = format(parseISO(from), 'yyyy-MM-dd');
    url = `${url}&from=${formattedFrom}`;

    try {
      const result = await this.httpTW.get(url);

      if (!Array.isArray(result.data) || result.data.length === 0) {
        return new HTTPResponse(
          availableTimeslotsResponse,
          Errors.SERVICE_NO_LONGER_POSSIBLE,
        );
      }

      const timeSlots: Slots[] = await toSlots(
        result.data,
        showTimeInStoreTZ(location),
      );

      availableTimeslotsResponse = {
        timeSlots,
      };

      return new HTTPResponse(availableTimeslotsResponse);
    } catch (err) {
      const { response } = err;
      if (!response) {
        return new HTTPResponse(
          availableTimeslotsResponse,
          Errors.SERVICE_NOT_AVAILABLE,
        );
      }

      const error =
        Object.values(Errors).indexOf(response.status) > -1
          ? response.status
          : Errors.SERVICE_NOT_AVAILABLE;
      return new HTTPResponse(availableTimeslotsResponse, error);
    }
  };

  public getAvailableStores = async (
    zip: string,
    serviceProductId: string,
  ): Promise<HTTPResponse<StoresResponse>> => {
    let availableStoresResponse: StoresResponse = {
      stores: [],
    };

    try {
      const url = `/locations/${zip}/${serviceProductId}`;
      const result = await this.httpTW.get(url);
      if (result.status < 200 || result.status >= 300) {
        // handle error
        const error =
          Object.values(Errors).indexOf(result.status) > -1
            ? result.status
            : Errors.SERVICE_NOT_AVAILABLE;
        return new HTTPResponse(availableStoresResponse, error);
      }

      if (!Array.isArray(result.data) || result.data.length === 0) {
        return new HTTPResponse(
          availableStoresResponse,
          Errors.SERVICE_NO_LONGER_POSSIBLE,
        );
      }

      const stores: AvailableStore[] = result.data.map(toAvailableStore);

      availableStoresResponse = {
        stores,
      };

      return new HTTPResponse(availableStoresResponse);
    } catch (err) {
      const { response } = err;
      if (!response) {
        return new HTTPResponse(
          availableStoresResponse,
          Errors.SERVICE_NOT_AVAILABLE,
        );
      }

      const error =
        Object.values(Errors).indexOf(response.status) > -1
          ? response.status
          : Errors.SERVICE_NOT_AVAILABLE;
      return new HTTPResponse(availableStoresResponse, error);
    }
  };

  public confirmAppointment = async (
    zipCode: string,
    serviceId: string,
    storeId: string,
    timeSlotId: string,
    from: string,
    serviceCode: string,
    payload: Payload,
  ): Promise<HTTPResponse<AppointmentResponse>> => {
    let appointmentResponse: AppointmentResponse = {
      orderNo: '',
      payOrderLink: '',
      questionsToken: '',
      payOrderLinkEnabled: true,
    };

    try {
      const { authToken } = await authenticateUser();
      const baseConfig: AxiosRequestConfig = {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
      };

      let url = `/services/${zipCode}/${serviceId}/${storeId}/${timeSlotId}?serviceCode=${serviceCode}`;

      const formattedFrom = format(parseISO(from), 'yyyy-MM-dd');
      url = `${url}&from=${formattedFrom}`;

      const result = await this.httpTW.post(url, payload, baseConfig);

      const { orderNo, payOrderLink, questionsToken, payOrderLinkEnabled } =
        result.data as AppointmentResponse;
      appointmentResponse = {
        orderNo,
        payOrderLink,
        questionsToken,
        payOrderLinkEnabled,
      };

      // 208 is a successful request; but we want to show the user an error.
      // So we return early with an "error object" so that we can derive the error status from the returned object.
      if (result.status === Errors.ALREADY_REPORTED) {
        return new HTTPResponse(appointmentResponse, {
          status: Errors.ALREADY_REPORTED,
          data: result.data,
        });
      }

      if (result.status === Errors.FORBIDDEN) {
        return new HTTPResponse(appointmentResponse, {
          status: Errors.FORBIDDEN,
          data: result.data,
        });
      }

      return new HTTPResponse(appointmentResponse);
    } catch (err) {
      const { response } = err;
      if (!response) {
        return new HTTPResponse(
          appointmentResponse,
          Errors.SERVICE_NOT_AVAILABLE,
        );
      }

      const error: ErrorResponse = {
        status:
          Object.values(Errors).indexOf(response.status) > -1
            ? response.status
            : Errors.SERVICE_NOT_AVAILABLE,
        data: response.data || {},
      };
      return new HTTPResponse(appointmentResponse, error);
    }
  };

  public async getPayOrderLink(
    orderNumber: string,
    email: string,
  ): Promise<string> {
    const encodedEmail = encodeURIComponent(email);
    try {
      const response: AxiosResponse<PayOrderLinkResponse> =
        await this.httpTW.get(
          `/payorderlink?orderNumber=${orderNumber}&email=${encodedEmail}`,
        );
      const link = response?.data?.link ?? '';
      return link;
    } catch (error) {
      return '';
    }
  }

  public getIANATimeZone = async (
    postalCode: string,
    serviceProductId: string,
  ): Promise<string> => {
    try {
      const response: AxiosResponse<string> = await this.httpBoka.get(
        `/services/timezone/${postalCode}/${serviceProductId}`,
      );

      const timeZone = response?.data;
      return timeZone;
    } catch (error) {
      return '';
    }
  };
}

export default Appointment;
